import { Features } from 'generated/featureDefinitions'
import { BankingAccountCreationAvailabilityDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

interface AccountTypeAvailability {
  canCreate: boolean
  reason: 'available' | 'needs_upsell' | 'no_permission'
  companyHasModule: boolean
  userHasPermission: boolean
}

interface BankingAccountCreationOptions {
  expenses: AccountTypeAvailability
  software: AccountTypeAvailability
  benefits: AccountTypeAvailability
  accountsPayable: AccountTypeAvailability
  payments: AccountTypeAvailability
  procurement: AccountTypeAvailability
}

export const useBankingAccountCreationAvailability = (): {
  loading: boolean
  options: BankingAccountCreationOptions
} => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data, loading } = useQuery(BankingAccountCreationAvailabilityDocument, {
    variables: {
      companyId,
      featureIds: [
        Features.expenses_core,
        Features.benefits_core,
        Features.accounts_payable_management,
        Features.finance_payments_core,
        Features.it_management_management,
        Features.procurement_core,
      ],
    },
  })

  const featureAvailabilities = data?.companies?.featureAvailabilitiesConnection?.nodes || []
  const featureFlags = data?.features?.featuresConnection?.nodes || []
  const bankingPolicies = data?.banking?._policies
  const expensesPolicies = data?.expenses?._policies

  const hasFeature = (featureId: Features): boolean => {
    return featureAvailabilities.find((f) => f.id === featureId)?.available ?? false
  }

  const hasFeatureFlag = (flagName: string): boolean => {
    return featureFlags.find((f) => f.name === flagName)?.enabled ?? false
  }

  /**
   * Determines availability based on company module ownership and user permissions.
   * Returns different reasons to allow UI to show upsell vs permission errors.
   */
  const calculateAvailability = (
    companyHasModule: boolean,
    userHasPermission: boolean
  ): AccountTypeAvailability => {
    if (!companyHasModule) {
      return {
        canCreate: false,
        companyHasModule: false,
        userHasPermission: false,
        reason: 'needs_upsell',
      }
    }

    if (!userHasPermission) {
      return {
        canCreate: false,
        companyHasModule: true,
        userHasPermission: false,
        reason: 'no_permission',
      }
    }

    return {
      canCreate: true,
      companyHasModule: true,
      userHasPermission: true,
      reason: 'available',
    }
  }

  const expensesAvailability = calculateAvailability(
    hasFeature(Features.expenses_core),
    (bankingPolicies?.bankAccountsManage?.can ?? false) &&
      (expensesPolicies?.cardManageCards?.can ?? false)
  )

  const softwareAvailability = calculateAvailability(
    hasFeature(Features.it_management_management),
    bankingPolicies?.bankAccountsManage?.can ?? false
  )

  const benefitsAvailability = calculateAvailability(
    hasFeature(Features.benefits_core),
    bankingPolicies?.bankAccountsManage?.can ?? false
  )

  const accountsPayableAvailability = calculateAvailability(
    hasFeature(Features.accounts_payable_management),
    bankingPolicies?.bankAccountsManage?.can ?? false
  )

  const paymentsAvailability = calculateAvailability(
    hasFeature(Features.finance_payments_core) || hasFeature(Features.accounts_payable_management),
    bankingPolicies?.bankAccountsManage?.can ?? false
  )

  // Procurement requires procurement_core + DEV flag + expenses_core (reuses Expenses account)
  const procurementAvailability = calculateAvailability(
    hasFeature(Features.procurement_core) &&
      hasFeatureFlag('DEV_PROCUREMENT_CARDS') &&
      hasFeature(Features.expenses_core),
    expensesPolicies?.cardManageCards?.can ?? false
  )

  return {
    loading,
    options: {
      expenses: expensesAvailability,
      software: softwareAvailability,
      benefits: benefitsAvailability,
      accountsPayable: accountsPayableAvailability,
      payments: paymentsAvailability,
      procurement: procurementAvailability,
    },
  }
}
